import { Fragment, useMemo } from "react";
import { useSelector } from "react-redux";

import * as Api from "@/api";
import Avatar from "@/design/components/Avatar";
import useAccessibleColors from "@/utils/hooks/useAccessibleColors";
import Valkyrie, { viLocationPinDot, viLock, viPause, viPlay, viTrashCan } from "@sippy-platform/valkyrie";

import { Divider, IconButton, List, ListItem, ListItemAvatar, ListItemText, Stack } from "@mui/material";
import PropTypes from "prop-types";

import ActivityBadge from "../components/ActivityBadge";

function MemberTab({ queue, setQueue }) {
  const colors = useAccessibleColors();

  const queueMemberStore = useSelector((state) => state.domain.queueMembers);

  function togglePlay(member) {
    Promise.resolve(Api.putQueueMember(queue, member, member.paused === 1 ? "unpause" : "pause")).then(() => {
      setQueue((prevQueue) => ({
        ...prevQueue,
        members: prevQueue.members.map((_member) => {
          if (_member.exten === member.exten) {
            _member.paused = member.paused === 1 ? 0 : 1;
          }

          return _member;
        }),
      }));
    });
  }

  function deleteMember(member) {
    Promise.resolve(Api.deleteQueueMember(queue, member)).then(() => {
      setQueue((prevQueue) => ({
        ...prevQueue,
        members: prevQueue.members.filter((_member) => _member.exten !== member.exten),
      }));
    });
  }

  const memberList = useMemo(() => {
    return queueMemberStore.list
      .map((member) => {
        const queueMember = queue.members.find((_queueMember) => _queueMember.exten === member.attributes.exten);

        if (queueMember) {
          return {
            exten: member.attributes.exten,
            name: member.attributes.name,
            type: member.type,
            paused: queueMember.paused,
            lock: queueMember.lock,
          };
        }
      })
      .filter((member) => member);
  }, [queueMemberStore.list, queue.members]);

  return (
    <List sx={{ gap: 0 }}>
      {memberList.length > 0 ? (
        memberList.map((member, key) => (
          <Fragment key={key}>
            {key >= 1 && (
              <Divider
                variant="inset"
                component="li"
                sx={{ listStyle: "none", ml: 6.5, mr: 1.5, borderColor: (theme) => theme.vars.palette.divider }}
              />
            )}
            <ListItem
              sx={{ minHeight: 36, py: 0.5 }}
              secondaryAction={
                <Stack
                  direction="row"
                  sx={{
                    gap: 0.5,
                  }}
                >
                  <IconButton size="small" onClick={() => togglePlay(member)} disabled={member.lock === 1}>
                    {member.lock === 1 ? (
                      <Valkyrie icon={viLock} />
                    ) : member.paused === 1 ? (
                      <Valkyrie icon={viPlay} />
                    ) : (
                      <Valkyrie icon={viPause} />
                    )}
                  </IconButton>
                  <IconButton size="small" onClick={() => deleteMember(member)}>
                    <Valkyrie icon={viTrashCan} />
                  </IconButton>
                </Stack>
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <ActivityBadge color={member.paused === 1 ? colors.red : colors.green} size="small">
                  <Avatar
                    text={member.name || ""}
                    icon={member.type === "user" ? undefined : viLocationPinDot}
                    px={24}
                  />
                </ActivityBadge>
              </ListItemAvatar>
              <ListItemText primary={member.name} />
            </ListItem>
          </Fragment>
        ))
      ) : (
        <ListItem
          sx={{
            minHeight: 36,
            py: 0.5,
          }}
        >
          <ListItemText primary="No members in queue" />
        </ListItem>
      )}
    </List>
  );
}

MemberTab.propTypes = {
  queue: PropTypes.object,
  setQueue: PropTypes.func,
};

export default MemberTab;
